import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import '../styles/PortfolioPage.css';

const PortfolioPage = () => {
  const { user } = useAuth();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  // Portfolio is now available to all users
  
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newProperty, setNewProperty] = useState({
    address: '',
    purchasePrice: '',
    monthlyRent: '',
    purchaseDate: '',
    notes: ''
  });

  useEffect(() => {
    // Wait for subscription to load before making decisions
    if (subscriptionLoading) return;
    
    if (user) {
      loadPortfolio();
    } else {
      setLoading(false);
    }
  }, [user, subscriptionLoading]);

  const loadPortfolio = () => {
    // Simulate loading portfolio from localStorage for now
    // In a real app, this would be from your backend
    const savedPortfolio = localStorage.getItem(`portfolio_${user?.userId}`) || '[]';
    setPortfolio(JSON.parse(savedPortfolio));
    setLoading(false);
  };

  const savePortfolio = (updatedPortfolio) => {
    localStorage.setItem(`portfolio_${user?.userId}`, JSON.stringify(updatedPortfolio));
    setPortfolio(updatedPortfolio);
  };

  const addProperty = () => {
    if (!newProperty.address || !newProperty.purchasePrice || !newProperty.monthlyRent) {
      alert('Please fill in all required fields');
      return;
    }

    const property = {
      id: Date.now(),
      ...newProperty,
      createdAt: new Date().toISOString(),
      capRate: calculateCapRate(newProperty.purchasePrice, newProperty.monthlyRent),
      annualIncome: parseFloat(newProperty.monthlyRent) * 12
    };

    const updatedPortfolio = [...portfolio, property];
    savePortfolio(updatedPortfolio);
    
    setNewProperty({ address: '', purchasePrice: '', monthlyRent: '', purchaseDate: '', notes: '' });
    setShowAddProperty(false);
  };

  const removeProperty = (id) => {
    if (window.confirm('Are you sure you want to remove this property from your portfolio?')) {
      const updatedPortfolio = portfolio.filter(p => p.id !== id);
      savePortfolio(updatedPortfolio);
    }
  };

  const calculateCapRate = (price, rent) => {
    const annualRent = parseFloat(rent) * 12;
    const purchasePrice = parseFloat(price);
    return purchasePrice > 0 ? ((annualRent / purchasePrice) * 100).toFixed(2) : '0.00';
  };

  const calculatePortfolioSummary = () => {
    return portfolio.reduce((summary, property) => {
      const price = parseFloat(property.purchasePrice) || 0;
      const rent = parseFloat(property.monthlyRent) || 0;
      
      return {
        totalValue: summary.totalValue + price,
        totalMonthlyIncome: summary.totalMonthlyIncome + rent,
        totalAnnualIncome: summary.totalAnnualIncome + (rent * 12),
        averageCapRate: 0, // Will calculate separately
        propertyCount: portfolio.length
      };
    }, { totalValue: 0, totalMonthlyIncome: 0, totalAnnualIncome: 0, averageCapRate: 0, propertyCount: 0 });
  };

  const formatCurrency = (value) => {
    return `$${parseFloat(value).toLocaleString()}`;
  };

  // Add debugging logs
  console.log('Portfolio Page - Debug Info:');
  console.log('User:', user);
  console.log('Subscription:', subscription);
  console.log('Subscription Loading:', subscriptionLoading);
  // Portfolio is now available to all authenticated users

  if (!user) {
    return (
      <div className="portfolio-page">
        <div className="auth-required">
          <h2>Portfolio Tracking</h2>
          <p>Please sign in to access your property portfolio.</p>
          <button onClick={() => navigate('/login')} className="btn-primary">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show loading while subscription data is being fetched
  if (subscriptionLoading) {
    return (
      <div className="portfolio-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading subscription data...</p>
        </div>
      </div>
    );
  }

  // Portfolio is now available to all authenticated users

  if (loading) {
    return (
      <div className="portfolio-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading your portfolio...</p>
        </div>
      </div>
    );
  }

  const summary = calculatePortfolioSummary();
  const averageCapRate = portfolio.length > 0 
    ? (portfolio.reduce((sum, p) => sum + parseFloat(p.capRate), 0) / portfolio.length).toFixed(2)
    : '0.00';

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <div className="header-content">
          <h1>My Property Portfolio</h1>
          <button 
            onClick={() => setShowAddProperty(true)}
            className="add-property-btn"
          >
            + Add Property
          </button>
        </div>
        
        <div className="premium-badge">
          <span className="badge-icon">⭐</span>
          <span>Premium Feature</span>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="portfolio-summary">
        <h3>Portfolio Overview</h3>
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-value">{summary.propertyCount}</div>
            <div className="summary-label">Properties</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{formatCurrency(summary.totalValue)}</div>
            <div className="summary-label">Total Value</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{formatCurrency(summary.totalMonthlyIncome)}</div>
            <div className="summary-label">Monthly Income</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{formatCurrency(summary.totalAnnualIncome)}</div>
            <div className="summary-label">Annual Income</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{averageCapRate}%</div>
            <div className="summary-label">Avg Cap Rate</div>
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className="properties-section">
        <h3>Properties ({portfolio.length})</h3>
        
        {portfolio.length === 0 ? (
          <div className="no-properties">
            <div className="no-properties-icon">🏡</div>
            <p>No properties in your portfolio yet.</p>
            <p>Add your first property to start tracking your real estate investments.</p>
            <button 
              onClick={() => setShowAddProperty(true)}
              className="add-first-property-btn"
            >
              Add Your First Property
            </button>
          </div>
        ) : (
          <div className="properties-grid">
            {portfolio.map((property) => (
              <div key={property.id} className="property-card">
                <div className="property-header">
                  <h4 className="property-address">{property.address}</h4>
                  <button 
                    onClick={() => removeProperty(property.id)}
                    className="remove-property-btn"
                  >
                    ×
                  </button>
                </div>
                
                <div className="property-metrics">
                  <div className="metric">
                    <span className="metric-label">Purchase Price</span>
                    <span className="metric-value">{formatCurrency(property.purchasePrice)}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Monthly Rent</span>
                    <span className="metric-value">{formatCurrency(property.monthlyRent)}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Cap Rate</span>
                    <span className="metric-value">{property.capRate}%</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Annual Income</span>
                    <span className="metric-value">{formatCurrency(property.annualIncome)}</span>
                  </div>
                </div>

                <div className="property-details">
                  {property.purchaseDate && (
                    <div className="detail">
                      <span className="detail-label">Purchase Date:</span>
                      <span className="detail-value">{new Date(property.purchaseDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {property.notes && (
                    <div className="detail">
                      <span className="detail-label">Notes:</span>
                      <span className="detail-value">{property.notes}</span>
                    </div>
                  )}
                </div>

                <div className="property-actions">
                  <button 
                    onClick={() => navigate('/form', { 
                      state: { 
                        address: property.address, 
                        isGuest: false 
                      } 
                    })}
                    className="analyze-btn"
                  >
                    Analyze
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Property Modal */}
      {showAddProperty && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Property</h3>
              <button 
                onClick={() => setShowAddProperty(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Property Address *</label>
                <input
                  type="text"
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                  placeholder="123 Main St, City, State"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Purchase Price *</label>
                  <input
                    type="number"
                    value={newProperty.purchasePrice}
                    onChange={(e) => setNewProperty({...newProperty, purchasePrice: e.target.value})}
                    placeholder="500000"
                  />
                </div>
                
                <div className="form-group">
                  <label>Monthly Rent *</label>
                  <input
                    type="number"
                    value={newProperty.monthlyRent}
                    onChange={(e) => setNewProperty({...newProperty, monthlyRent: e.target.value})}
                    placeholder="3000"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Purchase Date</label>
                <input
                  type="date"
                  value={newProperty.purchaseDate}
                  onChange={(e) => setNewProperty({...newProperty, purchaseDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newProperty.notes}
                  onChange={(e) => setNewProperty({...newProperty, notes: e.target.value})}
                  placeholder="Any additional notes about this property..."
                  rows="3"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => setShowAddProperty(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={addProperty}
                className="btn-primary"
              >
                Add Property
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;